/**
 * Helpers for the responsive portfolio-image variants produced by the
 * backend uploads pipeline.
 *
 * Every uploaded photo is re-encoded into three WebP variants that live
 * side-by-side in the same `<uuid>/` folder:
 *
 *   .../uploads/portfolio/<uuid>/thumb.webp    (~400px)
 *   .../uploads/portfolio/<uuid>/medium.webp   (~1000px)  ← stored as imageUrl
 *   .../uploads/portfolio/<uuid>/large.webp    (~1600px)
 *
 * The database keeps only the `medium` URL, so the thumb/large siblings
 * are derived here by swapping the filename. Anything that doesn't match
 * that pattern — legacy single-file uploads, admin-pasted external URLs,
 * bundled fallback assets — is returned untouched as a single source.
 */

/** Filename suffix of the canonical (medium) variant stored in the DB. */
const MEDIUM_SUFFIX = '/medium.webp';

/** Nominal widths of the variants — used as `srcset` width descriptors. */
const WIDTHS = { thumb: 400, medium: 1000, large: 1600 } as const;

export interface ImageSource {
  /** Always safe to use as a plain `src`. */
  src: string;
  /** Present only when responsive variants exist. */
  srcSet?: string;
}

interface Variants {
  thumb: string;
  medium: string;
  large: string;
}

/** Derive the three variant URLs, or null when `url` isn't a variant URL. */
function deriveVariants(url: string): Variants | null {
  if (!url.endsWith(MEDIUM_SUFFIX)) return null;
  const base = url.slice(0, -MEDIUM_SUFFIX.length);
  return {
    thumb: `${base}/thumb.webp`,
    medium: url,
    large: `${base}/large.webp`,
  };
}

/**
 * Build a responsive image source for a portfolio URL. Pair the returned
 * `src`/`srcSet` with a context-appropriate `sizes` attribute so the
 * browser downloads the smallest variant that fits the slot.
 *
 * Falls back to a single `src` for legacy / external / bundled images.
 */
export function imageSrcSet(url: string | null | undefined): ImageSource {
  if (!url) return { src: '' };
  const v = deriveVariants(url);
  if (!v) return { src: url };
  return {
    src: v.medium,
    srcSet: `${v.thumb} ${WIDTHS.thumb}w, ${v.medium} ${WIDTHS.medium}w, ${v.large} ${WIDTHS.large}w`,
  };
}

/** Best single URL for a full-bleed display (hero, lightbox). */
export function imageLargeSrc(url: string | null | undefined): string {
  if (!url) return '';
  return deriveVariants(url)?.large ?? url;
}

/** Smallest variant — for compact thumbnails where `medium` is overkill. */
export function imageThumbSrc(url: string | null | undefined): string {
  if (!url) return '';
  return deriveVariants(url)?.thumb ?? url;
}
